'use client'

import { Card } from '@acko/card'
import { Accordion } from '@acko/accordion'
import { Badge } from '@acko/badge'
import { Typography } from '@acko/typography'
import { ArrowRight, Tick, Close1, Plus } from '@acko/icons'
import { useState } from 'react'
import styles from '../page.module.css'
import { trackComprehensiveEngagement } from './ComprehensiveAnalyticsClient'

/* ─── Types (mirror parent InclusionsExclusionsSectionClient) ─────────────── */
type PlanCoverage = 'yes' | 'no' | 'addon'

type InclusionItem = {
  id: string
  title: string
  description?: string
  subPoints: string[]
  claimTip?: string
  plans: { comp: PlanCoverage; own: PlanCoverage; tp: PlanCoverage }
}

type ExclusionItem = {
  id: string
  title: string
  description?: string
  subPoints: string[]
  note?: string
}

type InclusionGroup = {
  groupLabel?: string
  groupNote?: string
  items: InclusionItem[]
}

/* ─── Plan Coverage Badge ─────────────────────────────────────────────────── */
const PlanBadge = ({ label, coverage }: { label: string; coverage: PlanCoverage }) => {
  if (coverage === 'yes') {
    return (
      <Badge color="green" variant="solid" size="md" textCase="sentence" icon={<Tick />}>
        {label}
      </Badge>
    )
  }
  if (coverage === 'addon') {
    return (
      <Badge color="purple" variant="solid" size="md" textCase="sentence" icon={<Plus />}>
        {label}
      </Badge>
    )
  }
  return (
    <Badge color="gray" variant="solid" size="md" textCase="sentence" icon={<Close1 />}>
      {label}
    </Badge>
  )
}

/* ─── Inclusions Data — one flat group, no labels (mirrors main bike-insurance page) ── */
const COMP_INCLUSION_GROUPS: InclusionGroup[] = [
  {
    items: [
      {
        id: 'ci-1',
        title: 'Repair costs for your own bike after any accident',
        description:
          'Comprehensive bike insurance covers the cost of repairs on your own bike if there is an accident, skid, or overturn. Third-party insurance does not cover any costs of repairing your own bike at all.',
        subPoints: [
          'Covers single-vehicle accidents: skidding, potholes, road dividers',
          'Covers two-vehicle collisions, even when you are at fault',
          'Covers bodywork, frame, suspension, and mechanical parts damaged in the crash',
          "ACKO settles cashless directly with the workshop; you don't pay the garage upfront",
        ],
        claimTip:
          'Capture pictures of all sides of the damage that has been caused to your bike. The ACKO mobile app lets you complete your claim in just 5 minutes!',
        plans: { comp: 'yes', own: 'yes', tp: 'no' },
      },
      {
        id: 'ci-2',
        title: 'Theft, burglary and partial theft of parts',
        description:
          'In case of theft of your bike, the comprehensive policy will pay you the total Insured Declared Value (IDV), which is the current market value of your bike less depreciation. There will be no coverage from third-party insurance in such a case.',
        subPoints: [
          'IDV will be paid in full once the Non-Traceable Certificate is issued by the police',
          'Part theft - wheels, mirrors, seats, is also claimable',
          'Always opt for a higher IDV while purchasing the bike',
          'Have the FIR copy, two sets of keys, and the RC book available for processing the claim',
        ],
        claimTip:
          'File an FIR within 24 hours of the discovery of the theft. ACKO Insurance will require the FIR number to initiate the theft claim process. Delay in registering the FIR may delay the process.',
        plans: { comp: 'yes', own: 'yes', tp: 'no' },
      },
      {
        id: 'ci-3',
        title: 'Fire, explosion and self-ignition',
        description:
          'Comprehensive cover pays for fire damage, whether caused by an engine fire, fuel line burst, electrical short circuit, or a lightning strike. Third-party insurance does not cover any fire damage to your own bike.',
        subPoints: [
          'Engine fires and fuel leaks that ignite are covered',
          'Electrical short circuits, causing fire, are covered',
          'Lightning strike damage is treated as a natural disaster under comprehensive cover',
          'Arson or deliberate fire is excluded; accidental fire is covered',
        ],
        plans: { comp: 'yes', own: 'yes', tp: 'no' },
      },
      {
        id: 'ci-4',
        title: 'Flood, earthquake and natural disasters',
        description:
          'Comprehensive coverage includes natural disasters and is often overlooked as a valuable feature. In a flood, a third-party-only bike is completely unprotected, whereas comprehensive insurance will cover all repairs to the bike.',
        subPoints: [
          'Covered incidents include: Flood, Cyclone, Earthquake, Landslide, Storm, Hailstorm',
          'Any damage due to falling trees/debris during the course of a storm is covered.',
          'Take photographs/videos of all damages before the water subsides or debris is removed.',
        ],
        claimTip:
          'Do not turn the engine on after flooding. Trying to start the engine after it is flooded results in irreparable hydrostatic lock, which falls under exclusions of a typical comprehensive policy. Get Engine Protect for this coverage.',
        plans: { comp: 'yes', own: 'yes', tp: 'no' },
      },
      {
        id: 'ci-8',
        title: 'Manmade disasters - riots, vandalism and strikes',
        subPoints: ['Damage to your two-wheeler caused by events such as vandalism, riots, strikes, etc.'],
        plans: { comp: 'yes', own: 'yes', tp: 'no' },
      },
      {
        id: 'ci-5',
        title: 'Cashless repairs at any authorised garage',
        description:
          'With comprehensive insurance, ACKO settles the repair charges directly with the garage. You approve the repairs and get your motorcycle back - no lump sum to pay, no waiting for money back.',
        subPoints: [
          'More than 5,400 authorised workshops all over India accept ACKO settlement',
          'Not required to pay to the garage and then claim the money back',
          'Also support non-cashless garages – make the payment and claim the money back within 7 days',
          'Cost of parts replaced from the workshop gets paid at the actual cost (unless Zero Dep is active)',
        ],
        plans: { comp: 'yes', own: 'yes', tp: 'no' },
      },
      {
        id: 'ci-6',
        title: 'Unlimited liability for third-party injury or death',
        description:
          'If your bike causes any harm or injury to another person, Indian law will deem you completely liable for their medical expenses with no maximum cap for liability. Comprehensive insurance, such as third-party insurance, covers you completely for this liability.',
        subPoints: [
          'No monetary cap on bodily injury or death liability under the Motor Vehicles Act',
          "Covers hospital costs, long-term rehabilitation, and compensation to victims' families",
          'Your insurer provides legal defence representation at the tribunal',
          'Riding without at least this cover is a criminal offence, with fines up to Rs. 2,000 and imprisonment',
        ],
        plans: { comp: 'yes', own: 'no', tp: 'yes' },
      },
      {
        id: 'ci-7',
        title: 'Third-party property damage up to Rs. 7.5 lakh',
        description:
          "If your bike damages another person's vehicle, building, or property in an accident, your comprehensive plan covers the repair or replacement cost up to Rs. 7.5 lakh per IRDAI regulation.",
        subPoints: [
          'Covers damage to parked vehicles, compound walls, shop fronts, and gates',
          'IRDAI cap of Rs. 7.5 lakh per incident, amounts above become your personal liability',
          'Covers damage caused at any speed, in any road condition',
          'A claim under this section does not impact your No Claim Bonus for own-damage cover',
        ],
        plans: { comp: 'yes', own: 'no', tp: 'yes' },
      },
    ],
  },
]

/* ─── Exclusions Data ─────────────────────────────────────────────────────── */
const COMP_EXCLUSIONS: ExclusionItem[] = [
  {
    id: 'ce-1',
    title: 'Engine damage from water ingression or oil leaks - without Engine Protect',
    description:
      'Comprehensive insurance policies exclude engine damage from riding into a flood and damage from oil seal failure. It is among the most common causes of the denial of comprehensive insurance claims.',
    subPoints: [
      'Hydrostatic lock from a flooded road causes total engine seizure, which is excluded by default',
      'Oil leak leading to engine damage is treated as a consequential loss is excluded by default',
      'Gearbox damage from water or lubricant breakdown is excluded',
    ],
    note: 'Fix this gap: Add the Engine Protect add-on from Rs. 45/year when buying your comprehensive plan.',
  },
  {
    id: 'ce-2',
    title: 'Depreciation deductions on replaced parts - without Zero Depreciation add-on',
    description:
      'Every standard comprehensive policy deducts depreciation from the value of replaced parts at claim time. The older your bike, the higher the deduction. Without Zero Dep, you pay this difference from your own pocket.',
    subPoints: [
      'Plastic parts: 50% depreciation deducted on bikes older than 5 years',
      'Rubber parts: up to 50% depreciation deducted regardless of bike age',
      'Metal parts: 10 to 40% depreciation based on bike age at claim',
    ],
    note: 'Fix this gap: Add the Zero Depreciation add-on for just Rs. 36/year; this is very economical for motorcycles under 5 years old.',
  },
  {
    id: 'ce-3',
    title: 'Consumables: engine oil, filters, coolant, brake fluid - without the consumables cover',
    description:
      "During a comprehensive claim repair, your bike's workshop usually provides consumables such as engine oil, oil filter, brake fluid, and coolant. However, such repair charges are not included in the standard comprehensives, so you have to pay for the items separately.",
    subPoints: [
      'Engine oil and oil filter replacements during a claim repair are excluded',
      'Brake fluid, coolant, and windshield washer fluid are excluded',
      'Lubricants used during part replacements are excluded',
    ],
    note: 'Fix this gap: Add a consumables cover for just ₹120/year; it can save you ₹500-₹1,500 per repair.',
  },
  {
    id: 'ce-4',
    title: 'Mechanical breakdown, wear and tear, and gradual deterioration',
    description:
      'Comprehensive insurance covers any losses that arise from any accident, but not from any wear and tear of your property. Some examples of wear and tear include engine wear, tyre balds, brake pads wear, battery discharge, and rust.',
    subPoints: [
      'Engine wear from high mileage or missed servicing is excluded',
      'Tyre failure from bald surfaces or sidewall cracking due to age is excluded',
      'Brake fade, or calliper seizure from neglected servicing, is excluded',
      'Regular maintenance at authorised service centres prevents most such claim rejections',
    ],
    note: "This isn't covered under any plan - comprehensive, own damage, or third-party.",
  },
  {
    id: 'ce-5',
    title: 'Accidents while riding drunk or without a valid driving licence',
    description:
      'If you have been part of an accident due to intoxication from alcohol and drugs, or while travelling without a valid driver’s license, your insurer would not just cut down on the amount but outrightly refuse your insurance claim.',
    subPoints: [
      'Blood alcohol above the legal limit at the time of the accident voids the claim entirely',
      'An expired driving licence is treated the same as no licence',
      "A learner's licence is only valid with a permanent licence holder on the pillion",
      'The exclusion applies to third-party payouts too, which creates personal unlimited liability',
    ],
    note: "This isn't covered under any plan - comprehensive, own damage, or third-party.",
  },
  {
    id: 'ce-6',
    title: 'Undeclared accessories and modifications to the bike',
    description:
      'Any accessory or modification not declared in your comprehensive policy at purchase or renewal is excluded from claims. This is increasingly relevant as riders fit aftermarket exhausts, LED lighting systems, or body kits.',
    subPoints: [
      'CNG or LPG kits must be declared and endorsed on the policy; undeclared kits void related claims',
      'Aftermarket alloy wheels, exhausts, and LED light bars must be declared',
      "Undeclared accessories inflate your bike's IDV. Not declaring will result in a lower theft payout",
      'Declare all modifications at the next renewal to ensure both the bike and accessories are covered',
    ],
    note: 'Modifications can affect comprehensive and own-damage claims in bike insurance. Declare any modifications when renewing your insurance cover so that your IDV remains secure.',
  },
  {
    id: 'ce-7',
    title: 'Consequential financial losses from an accident',
    description:
      'The comprehensive insurance covers damage to the bike, but it does not include loss of income because your bike is under repair, taxi fare, or business loss because of the unavailability of the bike.',
    subPoints: [
      'Lost earnings during repair downtime are not covered under any standard plan',
      'Taxi or rental costs incurred while your bike is at the garage are not covered by default',
      'Business delivery losses from bike unavailability are not covered',
      'Some comprehensive plans offer a Daily Allowance or Conveyance add-on; check your policy terms',
    ],
    note: 'Not covered under standard comprehensive, own damage, or third-party plans.',
  },
  {
    id: 'ce-8',
    title: 'Accidents outside India - including Nepal and Bhutan crossings',
    description:
      'Standard comprehensive bike insurance covers your bike only within India. This is a commonly missed exclusion among adventure riders who tour Nepal or Bhutan on their bikes.',
    subPoints: [
      'Coverage applies across all 28 states and 8 union territories of India',
      'Nepal and Bhutan crossings are not covered under standard Indian bike insurance',
      'Some insurers offer a short-term extension for Nepal and Bhutan on request. Check before your trip',
      'Any damage, theft, or third-party liability that occurs abroad is your full financial responsibility',
    ],
    note: 'Not covered under any standard Indian bike insurance policy, plan ahead if you tour internationally.',
  },
  {
    id: 'ce-9',
    title: 'Inactive policy',
    subPoints: ['Damages to your bike when your Bike Insurance Policy is not active.'],
  },
  {
    id: 'ce-10',
    title: 'Illegal activities',
    subPoints: ['Damages to your two-wheeler from unlawful activities such as racing.'],
  },
]

/* ─── Content builders ────────────────────────────────────────────────────── */
const inclusionItemContent = (item: InclusionItem) => (
  <div className="flex flex-col gap-14">
    {item.description && (
      <Typography scale="base" color="secondary">
        {item.description}
      </Typography>
    )}

    <ul className="flex flex-col gap-8 pl-4">
      {item.subPoints.map((point, i) => (
        <li key={i} className="flex gap-10 items-start">
          <span className="w-6 h-6 rounded-full bg-[var(--color-text-secondary,#888)] shrink-0 mt-[8px]" aria-hidden />
          <Typography scale="base" color="secondary" className="flex-1">
            {point}
          </Typography>
        </li>
      ))}
    </ul>

    {item.claimTip && (
      <Card variant="ghost" className={`flex gap-10 items-start p-16 ${styles.tipInfoSurface}`}>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          className={`shrink-0 mt-2 ${styles.tipInfoText}`}
          aria-hidden>
          <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.2" />
          <path d="M8 5v3.5M8 10.5v.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
        <Typography scale="base" className={`text-wrap ${styles.tipInfoText}`}>
          <span className="font-semibold">Claim tip: </span>
          {item.claimTip}
        </Typography>
      </Card>
    )}

    <div className="flex flex-wrap gap-8 pt-16 border-t border-[rgba(10,10,10,0.06)]">
      <PlanBadge label="Comprehensive" coverage={item.plans.comp} />
      <PlanBadge label="Own damage" coverage={item.plans.own} />
      <PlanBadge label="Third-party" coverage={item.plans.tp} />
    </div>
  </div>
)

const exclusionItemContent = (item: ExclusionItem) => (
  <div className="flex flex-col gap-14">
    {item.description && (
      <Typography scale="base" color="secondary">
        {item.description}
      </Typography>
    )}

    <ul className="flex flex-col gap-8 pl-4">
      {item.subPoints.map((point, i) => (
        <li key={i} className="flex gap-10 items-start">
          <span
            className="w-6 h-6 rounded-full bg-[var(--color-surface-warning,#f5a623)] shrink-0 mt-[8px]"
            aria-hidden
          />
          <Typography scale="base" color="secondary" className="flex-1">
            {point}
          </Typography>
        </li>
      ))}
    </ul>

    {item.note && (
      <div className="pt-16 border-t border-[rgba(10,10,10,0.06)]">
        <Typography scale="base" color="secondary">
          {item.note}
        </Typography>
      </div>
    )}
  </div>
)

/* ─── Main Section ────────────────────────────────────────────────────────── */
const TAB_INCLUSIONS = 'inclusions'
const TAB_EXCLUSIONS = 'exclusions'

const trackAccordionExpansion = (
  componentName: 'whats_covered' | 'whats_not_covered',
  items: Array<{ id: string; title: string }>,
  openValues: string[]
) => {
  const openedItem = items.find(item => item.id === openValues[0])
  if (openedItem) void trackComprehensiveEngagement(componentName, openedItem.title)
}

export const ComprehensiveInclusionsExclusionsSectionClient = () => {
  const [activeTab, setActiveTab] = useState<typeof TAB_INCLUSIONS | typeof TAB_EXCLUSIONS>(TAB_INCLUSIONS)

  return (
    <div id="comp-coverage" className="flex flex-col gap-32 lg:gap-40 w-full scroll-mt-[104px]">
      {/* Heading */}
      <div className="flex flex-col gap-8">
        <Typography scale="4xl" emphasis="bold" as="h2" color="primary">
          What comprehensive bike insurance covers
        </Typography>
        <Typography scale="base" color="secondary">
          The comprehensive plan provides the most protection for your bike compared to any other plan available in
          India. This is exactly what it protects: damage to your bike, third-party liability, and gaps you need to be
          aware of before making a claim.
        </Typography>
      </div>

      {/* Tab switcher */}
      <div
        className="flex gap-0 rounded-full p-4 w-full lg:w-fit"
        style={{
          backgroundColor: 'var(--color-surface-fill-default)',
          border: '1px solid var(--color-border-soft)',
        }}>
        {[
          { id: TAB_INCLUSIONS, label: "What's covered" },
          { id: TAB_EXCLUSIONS, label: "What's not covered" },
        ].map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => {
              void trackComprehensiveEngagement('coverage_tab', tab.label)
              setActiveTab(tab.id as typeof TAB_INCLUSIONS | typeof TAB_EXCLUSIONS)
            }}
            className="flex-1 lg:flex-none px-20 h-48 rounded-full font-medium transition-all duration-200 cursor-pointer flex items-center justify-center border-0"
            style={{
              fontSize: '14px',
              backgroundColor: activeTab === tab.id ? 'var(--color-accent-purple-surface)' : 'transparent',
              color: activeTab === tab.id ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
              boxShadow: activeTab === tab.id ? 'var(--shadow-xs)' : 'none',
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex flex-col gap-24 w-full">
        <div className={activeTab === TAB_INCLUSIONS ? 'contents' : 'hidden'}>
          {COMP_INCLUSION_GROUPS.map((group, gi) => (
            <div key={gi} className="flex flex-col gap-24">
              {group.groupLabel && (
                <div className="flex flex-col gap-4">
                  <Typography scale="xs" emphasis="bold" color="secondary" className="uppercase tracking-[1.5px]">
                    {group.groupLabel}
                  </Typography>
                  {group.groupNote && (
                    <Typography scale="base" color="secondary">
                      {group.groupNote}
                    </Typography>
                  )}
                </div>
              )}
              <Card variant="secondary" className="w-full px-24 py-16">
                <Accordion
                  type="single"
                  variant="lined"
                  size={'lg' as unknown as 'default'}
                  items={group.items.map(item => ({
                    value: item.id,
                    trigger: item.title,
                    content: inclusionItemContent(item),
                  }))}
                  onChange={openValues => trackAccordionExpansion('whats_covered', group.items, openValues)}
                />
              </Card>
            </div>
          ))}

          {/* Add-ons callout */}
          <Card variant="primary" className="p-20 md:p-24">
            <div className="flex flex-col gap-12">
              <Typography scale="xl" emphasis="bold" as="p" color="primary">
                Strengthen your comprehensive cover with add-ons
              </Typography>
              <Typography scale="base" color="secondary">
                Three gaps in standard comprehensive insurance — engine damage, depreciation deductions, and consumables
                — can be closed with targeted add-ons at a very low additional cost. Engine Protect, Zero Dep, and
                Consumables cover are available when you buy or renew your ACKO comprehensive plan.
              </Typography>
              <a
                href="#comp-add-ons"
                onClick={() => {
                  void trackComprehensiveEngagement('available_add_ons', 'See available add-ons')
                }}
                className="flex items-center gap-4 group cursor-pointer self-start no-underline text-inherit">
                <Typography scale="base" emphasis="medium" as="span" color="primary">
                  See available add-ons
                </Typography>
                <ArrowRight
                  aria-hidden
                  className="w-20 h-20 text-current transition-transform duration-200 group-hover:translate-x-4 shrink-0"
                />
              </a>
            </div>
          </Card>
        </div>
        <div className={activeTab === TAB_EXCLUSIONS ? 'contents' : 'hidden'}>
          <Card variant="secondary" className="w-full px-24 py-16">
            <Accordion
              type="single"
              variant="lined"
              size={'lg' as unknown as 'default'}
              items={COMP_EXCLUSIONS.map(item => ({
                value: item.id,
                trigger: item.title,
                content: exclusionItemContent(item),
              }))}
              onChange={openValues => trackAccordionExpansion('whats_not_covered', COMP_EXCLUSIONS, openValues)}
            />
          </Card>
        </div>
      </div>
    </div>
  )
}
